import { useApi } from './useApi'

const MAPS_DOMAIN_RE = /(?:maps\.google\.[a-z.]+|google\.[a-z.]+\/maps|maps\.app\.goo\.gl|goo\.gl\/maps)/i
const SHORT_LINK_RE = /(?:maps\.app\.goo\.gl|goo\.gl\/maps)\/\S+/i

const toLatLng = (latStr, lngStr) => {
  const lat = Number(latStr)
  const lng = Number(lngStr)
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null
  return { lat, lng }
}

const dmsToDecimal = (deg, min, sec, dir) => {
  const d = Number(deg)
  const m = Number(min)
  const s = Number(sec)
  if (![d, m, s].every(Number.isFinite)) return null
  const decimal = d + m / 60 + s / 3600
  return /^[SO]$/i.test(dir) ? -decimal : decimal
}

// Reconoce coordenadas pegadas como texto plano ("-26.18,-58.21") o extraídas de
// un link de Google Maps (viewport "@lat,lng", el pin exacto "!3d..!4d..", parámetros
// q=/query=/ll=, o coordenadas en grados/minutos/segundos del nombre del lugar).
export function parseGoogleMapsLocation(text) {
  const raw = String(text || '').trim()
  if (!raw) return null

  const plain = raw.match(/^(-?\d{1,3}(?:\.\d+)?)\s*,\s*(-?\d{1,3}(?:\.\d+)?)$/)
  if (plain) return toLatLng(plain[1], plain[2])

  if (!MAPS_DOMAIN_RE.test(raw)) return null

  // El pin marcado (!3d<lat>!4d<lng>) es más preciso que el centro del viewport.
  const pin = raw.match(/!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/)
  if (pin) return toLatLng(pin[1], pin[2])

  const viewport = raw.match(/@(-?\d{1,3}(?:\.\d+)?),(-?\d{1,3}(?:\.\d+)?)/)
  if (viewport) return toLatLng(viewport[1], viewport[2])

  const param = raw.match(/[?&](?:q|query|ll)=(-?\d{1,3}(?:\.\d+)?),(-?\d{1,3}(?:\.\d+)?)/)
  if (param) return toLatLng(param[1], param[2])

  let decoded = raw
  try { decoded = decodeURIComponent(raw) } catch { /* URL mal formada, seguimos con el texto crudo */ }
  const dms = decoded.match(
    /(\d{1,3})[°º]\s*(\d{1,2})['′]\s*([\d.]+)["″]\s*([NS])\D+(\d{1,3})[°º]\s*(\d{1,2})['′]\s*([\d.]+)["″]\s*([EWO])/i
  )
  if (dms) {
    const lat = dmsToDecimal(dms[1], dms[2], dms[3], dms[4])
    const lng = dmsToDecimal(dms[5], dms[6], dms[7], dms[8])
    if (lat != null && lng != null) return toLatLng(lat, lng)
  }

  return null
}

export function useGoogleMapsUrl() {
  const api = useApi()

  // Links cortos (maps.app.goo.gl, goo.gl/maps) no llevan coordenadas en el texto:
  // hay que resolver la redirección en el servidor y recién ahí parsear la URL final.
  async function resolveLocation(text) {
    const raw = String(text || '').trim()
    if (!raw) return null

    const direct = parseGoogleMapsLocation(raw)
    if (direct) return direct

    if (!SHORT_LINK_RE.test(raw)) return null
    try {
      const res = await api.get(`/api/geocode/resolve-url?url=${encodeURIComponent(raw)}`)
      return res?.url ? parseGoogleMapsLocation(res.url) : null
    } catch {
      return null
    }
  }

  return { resolveLocation }
}
